import React, { useState, useEffect } from 'react';
import '../styles/FooterManagement.css';

export function FooterManagement() {
  const [footerLinks, setFooterLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    section_name: '',
    link_text: '',
    link_url: '',
    is_public: 1,
    target: '_self',
    sort_order: 0,
    is_active: 1,
  });

  const token = localStorage.getItem('session_token');
  const headers = {
    'Content-Type': 'application/json',
    'X-Session-Token': token,
  };

  // Fetch footer links
  const fetchFooterLinks = () => {
    setLoading(true);
    fetch('/api/footer/admin', { headers })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setFooterLinks(data.links || []);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching footer links:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchFooterLinks();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.section_name.trim() || !formData.link_text.trim() || !formData.link_url.trim()) {
      setError('All fields are required');
      return;
    }

    try {
      const url = editingLink 
        ? `/api/footer/${editingLink.id}`
        : '/api/footer';
      
      const method = editingLink ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to save footer link');
        return;
      }

      setSuccess(data.message || 'Footer link saved successfully');
      setFormData({
        section_name: '',
        link_text: '',
        link_url: '',
        is_public: 1,
        target: '_self',
        sort_order: 0,
        is_active: 1,
      });
      setEditingLink(null);
      setShowForm(false);
      fetchFooterLinks();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving footer link:', err);
      setError('Connection error. Please try again.');
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      section_name: link.section_name,
      link_text: link.link_text,
      link_url: link.link_url,
      is_public: link.is_public,
      target: link.target,
      sort_order: link.sort_order,
      is_active: link.is_active,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this footer link?')) {
      return;
    }

    try {
      const response = await fetch(`/api/footer/${id}`, {
        method: 'DELETE',
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to delete footer link');
        return;
      }

      setSuccess('Footer link deleted successfully');
      fetchFooterLinks();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting footer link:', err);
      setError('Connection error. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditingLink(null);
    setFormData({
      section_name: '',
      link_text: '',
      link_url: '',
      is_public: 1,
      target: '_self',
      sort_order: 0,
      is_active: 1,
    });
    setShowForm(false);
    setError('');
  };

  // Group links by section
  const groupedLinks = {};
  footerLinks.forEach(link => {
    if (!groupedLinks[link.section_name]) {
      groupedLinks[link.section_name] = [];
    }
    groupedLinks[link.section_name].push(link);
  });

  return (
    <div className="footer-management">
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">⚙️ Manage Footer Links</h5>
          <button
            className="btn btn-light btn-sm"
            onClick={() => !showForm ? setShowForm(true) : handleCancel()}
          >
            {showForm ? '✕ Cancel' : '+ Add Link'}
          </button>
        </div>

        {error && <div className="alert alert-danger mb-3 m-3">{error}</div>}
        {success && <div className="alert alert-success mb-3 m-3">{success}</div>}

        {showForm && (
          <div className="card-body border-bottom">
            <h6 className="mb-3">{editingLink ? '✏️ Edit Footer Link' : '➕ Add New Footer Link'}</h6>
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Section Name *</label>
                  <input
                    type="text"
                    name="section_name"
                    className="form-control"
                    placeholder="e.g., About Us, Help & Support"
                    value={formData.section_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Link Text *</label>
                  <input
                    type="text"
                    name="link_text"
                    className="form-control"
                    placeholder="e.g., Contact Us"
                    value={formData.link_text}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Link URL *</label>
                  <input
                    type="text"
                    name="link_url"
                    className="form-control"
                    placeholder="e.g., /contact or https://example.com"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Target</label>
                  <select
                    name="target"
                    className="form-select"
                    value={formData.target}
                    onChange={handleInputChange}
                  >
                    <option value="_self">Same Window</option>
                    <option value="_blank">New Window</option>
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Sort Order</label>
                  <input
                    type="number"
                    name="sort_order"
                    className="form-control"
                    value={formData.sort_order}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      id="is_public"
                      name="is_public"
                      className="form-check-input"
                      checked={formData.is_public === 1}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="is_public">
                      Public
                    </label>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      className="form-check-input"
                      checked={formData.is_active === 1}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                  💾 Save Link
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : Object.keys(groupedLinks).length === 0 ? (
            <div className="alert alert-info mb-0">
              No footer links yet. Click "Add Link" to create one.
            </div>
          ) : (
            <div>
              {Object.keys(groupedLinks).sort().map(section => (
                <div key={section} className="mb-4 pb-4 border-bottom">
                  <h6 className="fw-bold text-primary mb-3">📌 {section}</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Link Text</th>
                          <th>URL</th>
                          <th>Target</th>
                          <th>Order</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedLinks[section].sort((a, b) => a.sort_order - b.sort_order).map(link => (
                          <tr key={link.id}>
                            <td>
                              <strong>{link.link_text}</strong>
                            </td>
                            <td className="text-muted small">
                              {link.link_url.length > 30 
                                ? link.link_url.substring(0, 30) + '...' 
                                : link.link_url}
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {link.target === '_blank' ? '🔗 New' : '↪️ Same'}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{link.sort_order}</span>
                            </td>
                            <td>
                              <span className={`badge ${link.is_active ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {link.is_active ? '✓ Active' : '⊘ Inactive'}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary me-2"
                                onClick={() => handleEdit(link)}
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(link.id)}
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FooterManagement;
